/**
 * Floating name labels over every character's head — NPCs and the player.
 *
 * Janeks: *"add NPC/Player names over their heads. (friendlies Green,
 * Enemies - red)"*.
 *
 * ─── Three colours: who it is, not how important it is ──────────────────────
 * Janeks: *"NPCs - make them Blue. Players friendly - green (including
 * player), enemies all - red."* So the split is by KIND of body:
 *
 *   `player`  green — the local player, and any friendly player later
 *   `npc`     blue  — every non-hostile NPC, quest-giver and passer-by alike
 *   `hostile` red   — anything flagged `WorldObject.hostile`, NPC or player
 *
 * The green key is `player`, not `friendly`, on purpose: renaming it is what
 * made the compiler point at `setPlayerName`'s hardcoded literal, which is the
 * single call site that decides the player's own colour and had no test.
 *
 * Only two of the three render today. Nothing in the rendered game is hostile:
 * the settlement holds friendlies only, `location-view.ts` says so in its own
 * header ("Free-roam + loot only (no enemies)"), `LocationScenario.enemySet` is
 * read nowhere, and the raid resolver has no coordinate concept at all. The red
 * path is driven by `WorldObject.hostile` and was verified by flagging a body
 * temporarily, since no shipped map sets it.
 *
 * The flag deliberately lives on `WorldObject`, NOT on `Npc`: a future enemy
 * is a placed body with no entry in `WorldData.npcs` — exactly the shape the
 * sixteen ambient citizens already have — so `Npc.hostile` would be
 * unreachable for the very things it is for.
 *
 * ─── Two name sources, because `label` is not a name ────────────────────────
 * Eight of the twenty-four `kind: 'npc'` bodies in New Ashford carry an
 * `npcId`, and those resolve to a real person in `WorldData.npcs`. The other
 * sixteen are ambient citizens whose `label` describes what they are DOING
 * ("Sleeping Off a Shift", "Market Gossip", "Trading with Marek", "Fireside") —
 * rendering that over someone's head reads as a bug, so they get one generic
 * `AMBIENT_NAME` instead (`HOSTILE_NAME` for an enemy — a red "Settler" reads
 * as a miscoloured neighbour, not as an unnamed enemy). No rule separates the role-ish labels ("Tower Guard",
 * "Militia Patrol") from the activity ones, so no attempt is made to.
 *
 * ─── One treatment for every tag, and a quiet one ───────────────────────────
 * The crowd used to be drawn smaller, lighter and dimmer than a body with a
 * real name. Janeks, seeing it in the world: *"Settlers names are simplified,
 * when player and interactable NPC are not, all should be the same."* So there
 * is now exactly ONE way a name is drawn — same size, same weight, same
 * outline, same opacity — whoever is wearing it. The only thing that still
 * varies is the COLOUR, which is faction, not hierarchy.
 *
 * Levelling the crowd UP to the named treatment was the wrong direction:
 * *"what u did is you made all settler names same as interactable — bold and
 * standing out. Make all of them thin and lesser in the UI. Too bulky, too
 * much now."* Uniformity was the half worth keeping, so the whole set came
 * down together — regular weight instead of bold, a smaller card, and a
 * ceiling below full opacity (`TAG_MAX_OPACITY`). The crowd did NOT go back to
 * being painted differently from a named NPC.
 *
 * `STROKE_WIDTH` deliberately did not come down with the rest. It is in the
 * card's own user units, so shrinking the card already thins the outline on
 * screen; cutting the number too would have compounded, and that outline is
 * the only thing holding green type apart from lit ground and dust.
 *
 * `isGenericName` survives all of it: it answers a question about the DATA (is
 * this body wearing a fallback word, or its own name?) and `resolveNameTags`
 * still reports it. It just no longer changes how the tag is painted.
 *
 * ─── Distance fade ──────────────────────────────────────────────────────────
 * Sixteen identical "Settler" tags would be noise if they drew at unlimited
 * range. They fade out between `TAG_FULL_RANGE` and `TAG_FADE_RANGE` metres
 * from the PLAYER (not the camera — this is about who is near you, and the
 * camera's own 10-26 m orbit would otherwise make the cutoff move when you
 * zoom). That fade is what makes the generic label affordable.
 *
 * ─── Stacking with the quest markers ────────────────────────────────────────
 * Both hang off the same anchor. The name sits on the head and the `!`/`?`
 * floats above it, which is the conventional order and the only one that
 * survives an NPC having both: the name card spans `[h+0.12, h+0.56]`, so
 * `quest-markers.ts`'s `MARKER_CLEARANCE` (1.05) keeps its glyph clear above
 * that — with more air than it was tuned for, since the card shrank under it
 * and the marker did not. Sized in world metres (`sizeAttenuation` left on,
 * like the markers) so that stacking is fixed rather than sliding with zoom.
 *
 * ─── Glyphs are generated, not shipped ──────────────────────────────────────
 * Same inline-SVG-data-URI approach as `quest-markers.ts`, and for the same
 * reason: it keeps the text out of the asset pipeline and makes it testable
 * without a GPU (`nameTagCard` is a pure function). A data-URI SVG cannot pull
 * a webfont, so this uses a generic bold sans rather than the Codex display
 * face, and the card's width is ESTIMATED from the text (`textWidthEm`)
 * instead of measured — the sprite's aspect is set from that estimate, so an
 * over-estimate shows as a little extra transparent air and never as squashed
 * or clipped type.
 */
import * as THREE from 'three';
import type { ActorPose, Vec2, WorldData, WorldObject } from '../types';

export type TagFaction = 'player' | 'npc' | 'hostile';

/**
 * Codex palette. `player` is `--terminal` (the CRT green) verbatim; `hostile`
 * is `--rust` (#a8452c) brightened; `npc` is a blue with no token behind it at
 * all — every candidate in the theme is a dark UI fill, and a name has to read
 * at a dozen metres against BOTH lit sand and near-black asphalt, which is the
 * constraint that picked this value rather than the palette.
 *
 * Literal hex rather than a CSS variable for the same reason
 * `quest-markers.ts` uses one: this ends up inside an SVG baked into a
 * texture, where `var(--terminal)` resolves to nothing.
 */
export const TAG_COLORS: Record<TagFaction, string> = {
  player: '#6fdc8f',
  npc: '#74baf5',
  hostile: '#e8543a',
};

/** What a FRIENDLY body with no entry in `WorldData.npcs` is called. See the header. */
export const AMBIENT_NAME = 'Settler';
/**
 * The same fallback for a hostile body.
 *
 * Split from `AMBIENT_NAME` after seeing it rendered: an unnamed enemy came up
 * as a red "Settler", which reads as a bug in the colour rather than as an
 * unnamed enemy. The generic word has to agree with the colour, since between
 * them they are the entire label.
 */
export const HOSTILE_NAME = 'Hostile';

/** Shown when the account has no username — see `playerDisplayName`. */
export const DEFAULT_PLAYER_NAME = 'Survivor';

/** Metres above the anchor's own height the card is centred. */
const NAME_CLEARANCE = 0.34;
/**
 * World-space height of the name card.
 *
 * 0.62 (the quest marker's own size) -> 0.44 -> 0.34, each step asked for after
 * seeing the last one in the world. At 0.34 the cap height is ~12 px at the
 * 17 m end of the zoom clamp, which is about the floor for a generic sans with
 * an outline; going further would need the outline rethought, not just the
 * number changed. See the header.
 */
const NAME_HEIGHT = 0.34;
/**
 * Regular, not bold (it was 700) — see the header.
 *
 * 400 rather than a true light weight because the card is an SVG data URI,
 * which cannot pull a webfont: this resolves to the platform's
 * Helvetica/Arial, neither of which ships a 300. A browser asked for one
 * either snaps back to 400 anyway or synthesises a thinned outline, and a
 * synthesised thin breaks up at the distance these are read from.
 */
const NAME_WEIGHT = 400;
/**
 * Ceiling on a tag's opacity, applied on top of the distance ramp.
 *
 * These labels are ambient information, not UI the player is deliberately
 * reading, and two dozen of them at full strength compete with the world.
 * Applied at the two places a material opacity is written — NOT inside
 * `tagOpacityForDistance`, which stays a pure 0..1 ramp answering "how far
 * away", not "how loud".
 */
export const TAG_MAX_OPACITY = 0.78;
/** Fallback anchor height when a body declares neither `modelHeight` nor `size.h`. */
const DEFAULT_ANCHOR_HEIGHT = 1.8;
/**
 * The player is not a `WorldObject`, so its head height is this constant
 * rather than a measurement. Deliberately NOT a `Box3` of the player model:
 * that is an async GLB clone which is frequently not loaded yet on the frames
 * that matter — the same class of bug `index.ts` already documents for the
 * muzzle-flash bone lookup. Every placed body declares `size.h: 1.8`, and the
 * player uses the same character GLBs.
 */
export const PLAYER_ANCHOR_HEIGHT = 1.8;

/** Metres from the player within which a tag is fully opaque. */
export const TAG_FULL_RANGE = 11;
/** Metres from the player beyond which a tag is not drawn at all. */
export const TAG_FADE_RANGE = 18;

/** Below everything `quest-markers.ts` draws, above the world. */
const TAG_RENDER_ORDER = 19;

// ---- SVG card geometry (user units; only ratios matter) ----
const FONT_SIZE = 64;
/** Tall enough that the outline stroke clears the caps above and descenders below. */
const CARD_HEIGHT = 88;
const BASELINE_Y = 68;
const STROKE_WIDTH = 7;
/** Transparent air either side of the text, in em. */
const SIDE_PADDING_EM = 0.32;

/**
 * Rough advance width of one character, in em, for a bold generic sans.
 *
 * An estimate, not a measurement — see the header. It only has to be an
 * OVER-estimate on average: the text is centred in the card, so slack shows
 * as symmetric transparent padding, while a shortfall would push long names
 * past the card edge where the sprite's aspect would squash them.
 */
export function advanceEm(ch: string): number {
  if ('ijltfI.,:;\'"!|()[]{} '.includes(ch)) return 0.34;
  if ('MWmw@#%'.includes(ch)) return 0.88;
  if (ch >= 'A' && ch <= 'Z') return 0.72;
  return 0.62;
}

/** Estimated width of `text` in em, via `advanceEm`. */
export function textWidthEm(text: string): number {
  let w = 0;
  for (const ch of text) w += advanceEm(ch);
  return w;
}

export interface NameTagCard {
  /** SVG data URI for the card's texture. */
  url: string;
  /** width / height — the sprite's scale.x is scale.y times this. */
  aspect: number;
}

/**
 * The name card as an SVG data URI, plus the aspect its sprite must use.
 *
 * `paint-order="stroke"` draws the dark outline BEHIND the fill instead of
 * over it, so the letters keep their full weight — without it the stroke eats
 * into thin strokes and a name at range turns to mush. That matters more now
 * that the type is regular weight rather than bold: there is less glyph for
 * the stroke to eat. Same trick, same reason, as `markerGlyphDataUrl`.
 */
export function nameTagCard(text: string, faction: TagFaction): NameTagCard {
  const width = Math.max(
    1,
    Math.round((textWidthEm(text) + SIDE_PADDING_EM) * FONT_SIZE),
  );
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${CARD_HEIGHT}"`
    + ` viewBox="0 0 ${width} ${CARD_HEIGHT}">`
    + `<text x="${width / 2}" y="${BASELINE_Y}" text-anchor="middle"`
    + ` font-size="${FONT_SIZE}" font-weight="${NAME_WEIGHT}"`
    + ' font-family="Helvetica, Arial, sans-serif"'
    + ` stroke="#15120c" stroke-width="${STROKE_WIDTH}" stroke-linejoin="round"`
    + ` paint-order="stroke" fill="${TAG_COLORS[faction]}">`
    + `${escapeXml(text)}</text></svg>`;
  return {
    url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
    aspect: width / CARD_HEIGHT,
  };
}

/** A username lands in markup here, and `&` in one would break the document. */
function escapeXml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[c] as string
  ));
}

/** Opacity for a tag `distance` metres from the player. Pure. */
export function tagOpacityForDistance(distance: number): number {
  if (distance <= TAG_FULL_RANGE) return 1;
  if (distance >= TAG_FADE_RANGE) return 0;
  return (TAG_FADE_RANGE - distance) / (TAG_FADE_RANGE - TAG_FULL_RANGE);
}

/**
 * What to call the signed-in player.
 *
 * `AuthUser.username` is `string | null` (registration takes it as optional),
 * so this is a chain, not a read. The email local-part beats a generic
 * fallback — it is at least the player's own word for themselves.
 */
export function playerDisplayName(
  user: { username?: string | null; email?: string | null } | null | undefined,
): string {
  const username = user?.username?.trim();
  if (username) return username;
  const local = user?.email?.split('@')[0]?.trim();
  if (local) return local;
  return DEFAULT_PLAYER_NAME;
}

export interface NameTagAnchor {
  objectId: string;
  text: string;
  faction: TagFaction;
  /**
   * True when `text` is a fallback word rather than this person's own name.
   *
   * Descriptive only — it does NOT change how the tag is drawn any more (see
   * the header). Kept because it is a real fact about the body that callers
   * other than the painter may want.
   */
  generic: boolean;
  position: Vec2;
  /** Top of the body, before `NAME_CLEARANCE`. */
  height: number;
}

/**
 * The faction of a PLACED BODY, which is never `player` — the local player is
 * not a `WorldObject` and gets its colour in `setPlayerName`. `hostile` is
 * optional and absent everywhere today; see the header.
 */
export function factionOf(obj: Pick<WorldObject, 'hostile'>): TagFaction {
  return obj.hostile ? 'hostile' : 'npc';
}

/**
 * The name shown over one placed body: the person's real name when the body
 * carries an `npcId` that resolves, else `AMBIENT_NAME`.
 *
 * An `npcId` that does NOT resolve falls through to the generic name rather
 * than rendering the raw key: a key over someone's head is worse than a
 * generic word, and the map's own `validateWorld` is where a dangling id
 * should be caught, not here.
 *
 * `displayName` wins over everything — it is the per-body override the map
 * editor writes for the crowd, which has no `npcs` entry to name.
 */
export function nameForBody(world: WorldData, obj: WorldObject): string {
  const own = obj.displayName?.trim();
  if (own) return own;
  const npc = obj.npcId ? world.npcs[obj.npcId] : undefined;
  return npc?.name?.trim() || (obj.hostile ? HOSTILE_NAME : AMBIENT_NAME);
}

/** True when this body is showing a fallback word rather than a name of its own. */
export function isGenericName(world: WorldData, obj: WorldObject): boolean {
  if (obj.displayName?.trim()) return false;
  return !obj.npcId || !world.npcs[obj.npcId]?.name?.trim();
}

/** One anchor per `kind: 'npc'` body in the world. Pure. */
export function resolveNameTags(world: WorldData): NameTagAnchor[] {
  return world.objects
    .filter((obj) => obj.kind === 'npc')
    .map((obj) => ({
      objectId: obj.id,
      text: nameForBody(world, obj),
      faction: factionOf(obj),
      generic: isGenericName(world, obj),
      position: { x: obj.position.x, z: obj.position.z },
      height: (obj.positionY ?? 0) + (obj.modelHeight ?? obj.size.h ?? DEFAULT_ANCHOR_HEIGHT),
    }));
}

export interface NameTags {
  /** Add this to the scene once; everything below mutates its children. */
  readonly object: THREE.Group;
  /**
   * Sets the local player's own label. Safe to call repeatedly with the same
   * value (no-ops) — it is driven by an auth-store subscription that fires on
   * every push, not only when the name changes. `null` removes it.
   */
  setPlayerName(name: string | null): void;
  /** Per-frame: follow live actor poses and fade by distance from the player. */
  update(playerPos: Vec2, actors: readonly ActorPose[]): void;
  dispose(): void;
}

interface LiveTag {
  sprite: THREE.Sprite;
  anchor: NameTagAnchor;
}

export function createNameTags(world: WorldData): NameTags {
  const group = new THREE.Group();
  group.name = 'name-tags';
  const loader = new THREE.TextureLoader();
  /** Keyed by faction + text, so sixteen "Settler" tags share one texture. */
  const textures = new Map<string, THREE.Texture>();
  const tags: LiveTag[] = [];
  let playerTag: THREE.Sprite | null = null;
  let playerName: string | null = null;

  const buildSprite = (text: string, faction: TagFaction): THREE.Sprite => {
    // Faction + text is the whole identity of a card now that every tag is
    // drawn the same way — sixteen "Settler" tags really are one image.
    const key = `${faction}|${text}`;
    let tex = textures.get(key);
    const card = nameTagCard(text, faction);
    if (!tex) {
      tex = loader.load(card.url);
      tex.colorSpace = THREE.SRGBColorSpace;
      textures.set(key, tex);
    }
    const material = new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      // The ceiling has to be set HERE, not only in `update()`: that loop
      // writes an opacity for every body tag but the player's own tag is
      // positioned and nothing else, so a cap applied only there would leave
      // the player wearing the one tag still at full strength — precisely the
      // inconsistency this whole round is a correction for.
      opacity: TAG_MAX_OPACITY,
      // Legible through a wall or a tree, like the quest markers: a name is
      // wayfinding, and one that half-disappears behind geometry reads as a
      // rendering fault rather than as occlusion.
      depthTest: false,
      depthWrite: false,
      // Deliberately NOT fog-affected — this settlement has fog zones, and a
      // name fading into one is indistinguishable from no name at all.
      fog: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(NAME_HEIGHT * card.aspect, NAME_HEIGHT, 1);
    sprite.renderOrder = TAG_RENDER_ORDER;
    sprite.userData.text = text;
    sprite.userData.faction = faction;
    return sprite;
  };

  for (const anchor of resolveNameTags(world)) {
    const sprite = buildSprite(anchor.text, anchor.faction);
    sprite.name = `name-tag:${anchor.objectId}`;
    sprite.position.set(anchor.position.x, anchor.height + NAME_CLEARANCE, anchor.position.z);
    group.add(sprite);
    tags.push({ sprite, anchor });
  }

  const disposeSprite = (sprite: THREE.Sprite): void => {
    group.remove(sprite);
    sprite.material.dispose();
  };

  const setPlayerName = (name: string | null): void => {
    const next = name?.trim() || null;
    if (next === playerName) return;
    playerName = next;
    if (playerTag) {
      disposeSprite(playerTag);
      playerTag = null;
    }
    if (!next) return;
    // Green, and the only thing in the world that gets it — `factionOf` can
    // never return `player`, so this literal is the whole of that branch.
    playerTag = buildSprite(next, 'player');
    playerTag.name = 'name-tag:player';
    group.add(playerTag);
  };

  const update = (playerPos: Vec2, actors: readonly ActorPose[]): void => {
    // Built per call rather than cached: `actors` is a fresh array from the
    // sim every frame and the set is small — the same trade `quest-markers.ts`
    // makes, for the same reason.
    const live = new Map<string, ActorPose>();
    for (const a of actors) live.set(a.objectId, a);

    for (const { sprite, anchor } of tags) {
      const pose = live.get(anchor.objectId);
      // A hidden body (`RoutineStep.hidden` — asleep indoors) is not drawn, so
      // a name floating over the roof would be labelling nobody.
      if (pose?.hidden) {
        sprite.visible = false;
        continue;
      }
      const x = pose ? pose.position.x : anchor.position.x;
      const z = pose ? pose.position.z : anchor.position.z;
      // Distance is the ONLY thing that fades a tag now — a settler standing
      // beside a quest-giver is drawn exactly as strongly.
      const opacity = tagOpacityForDistance(Math.hypot(x - playerPos.x, z - playerPos.z));
      sprite.visible = opacity > 0;
      if (!sprite.visible) continue;
      sprite.material.opacity = opacity * TAG_MAX_OPACITY;
      sprite.position.set(x, anchor.height + NAME_CLEARANCE, z);
    }

    if (playerTag) {
      playerTag.position.set(
        playerPos.x,
        PLAYER_ANCHOR_HEIGHT + NAME_CLEARANCE,
        playerPos.z,
      );
    }
  };

  return {
    object: group,
    setPlayerName,
    update,
    dispose() {
      for (const { sprite } of tags) disposeSprite(sprite);
      tags.length = 0;
      if (playerTag) disposeSprite(playerTag);
      playerTag = null;
      for (const tex of textures.values()) tex.dispose();
      textures.clear();
    },
  };
}
